import {
  bffCrmAssigneeGroupsMembers,
  bffCrmAssigneeGroupsMembersRemove,
  bffCrmMeAssigneeGroups,
  userAssigneeGroup,
  UserAssigneeGroupListResponse,
  type BffCrmAssigneeGroupsMembersRetrieveResponse,
  type BffCrmMeAssigneeGroupsRetrieveResponse,
} from '@fireflyhealth/api'
import { useMutation, useQuery } from '~/shared/hooks/query'
import { queryClient } from '~/shared/lib/queryClient'
import { required } from '~/shared/lib/required'
export type AssigneeGroupListItem = UserAssigneeGroupListResponse[number]

export async function fetchAssigneeGroups(): Promise<AssigneeGroupListItem[]> {
  // groups_only=true returns only true groups (excludes per-user assignee groups),
  // matching the original behavior the CRM relies on (e.g. the Inbox owner-group filter).
  const response = await userAssigneeGroup.list({ params: { groups_only: true } })
  return response.data
}

type AssigneeGroupMembers = BffCrmAssigneeGroupsMembersRetrieveResponse

const assigneeGroupMembersQueryKey = (groupId: number) =>
  bffCrmAssigneeGroupsMembers.retrieveQueryKey({ group_id: groupId })

export const useAssigneeGroupMembers = (groupId: number | undefined) =>
  useQuery<AssigneeGroupMembers>(
    groupId != null ? assigneeGroupMembersQueryKey(groupId) : ['bffCrmAssigneeGroupsMembers', {}],
    () =>
      bffCrmAssigneeGroupsMembers
        .retrieve({ group_id: required(groupId, 'a group id') })
        .then(r => r.data),
    { enabled: groupId != null },
    { error: 'Failed to load group members' },
  )

const invalidateMembers = (groupId: number) =>
  queryClient.invalidateQueries({ queryKey: assigneeGroupMembersQueryKey(groupId) })

export const useAddAssigneeGroupMember = (groupId: number) =>
  useMutation(
    (userId: number) =>
      bffCrmAssigneeGroupsMembers
        .create({ group_id: groupId }, { data: { user_id: userId } })
        .then(r => r.data),
    { onSuccess: () => invalidateMembers(groupId) },
    { error: 'Failed to add member', success: 'Member added' },
  )

export const useRemoveAssigneeGroupMember = (groupId: number) =>
  useMutation(
    (userId: number) =>
      bffCrmAssigneeGroupsMembersRemove
        .create({ group_id: groupId }, { data: { user_id: userId } })
        .then(r => r.data),
    { onSuccess: () => invalidateMembers(groupId) },
    { error: 'Failed to remove member', success: 'Member removed' },
  )

/** The caller's own group memberships, split into `personal` / `covering` / `teams`. */
export type MyAssigneeGroups = BffCrmMeAssigneeGroupsRetrieveResponse

/** The caller's own memberships, for the Inbox coverage sections and quick-add chips. */
export const useMyAssigneeGroups = () =>
  useQuery<MyAssigneeGroups>(
    bffCrmMeAssigneeGroups.retrieveQueryKey(),
    () => bffCrmMeAssigneeGroups.retrieve().then(r => r.data),
    undefined,
    { error: 'Failed to load your groups' },
  )
